import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { PageMeta, PageHero, Section, SectionHeading, FeatureCard, CtaBlock, Pill } from "@/components/PageShellParts";
import { AlertTriangle, ClipboardCheck, ClipboardList, Compass, FileSearch, GraduationCap, HeartHandshake, Lock, Sparkles, Users } from "lucide-react";

const Icons = { AlertTriangle, ClipboardCheck, ClipboardList, Compass, FileSearch, GraduationCap, HeartHandshake, Lock, Sparkles, Users };

export default function ForEducationPage() {
  return (
    <main className="min-h-screen bg-white">
      <Navbar />
      <PageMeta title="For Education" description="Privacy-aware mental health assessment support for schools, universities, and student wellbeing teams." />
      <div className="pt-20">
      <PageHero
        eyebrow="For Education"
        title="Mental Health Assessment Support for Education"
        subtitle="PsyTech can help educational institutions explore structured, privacy-aware support workflows for students and wellbeing teams."
      >
        <div className="flex flex-wrap gap-2">
          <Pill tone="cyan">Student-friendly</Pill>
          <Pill tone="cyan">Privacy-aware</Pill>
          <Pill tone="cyan">Team review workflows</Pill>
        </div>
      </PageHero>

      <Section>
        <SectionHeading eyebrow="Pain points" title="What wellbeing teams commonly face" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.Users} title="Growing student wellbeing needs">Demand for support continues to rise across schools and universities.</FeatureCard>
          <FeatureCard icon={Icons.HeartHandshake} title="Limited support capacity">Wellbeing teams are stretched thin relative to the cases they face.</FeatureCard>
          <FeatureCard icon={Icons.Compass} title="Hard to identify who needs support">Informal channels make it difficult to see the full picture.</FeatureCard>
          <FeatureCard icon={Icons.ClipboardList} title="Inconsistent intake information">Each case often starts from scratch instead of a shared structured baseline.</FeatureCard>
          <FeatureCard icon={Icons.Lock} title="Privacy-sensitive environments">Sensitive student data requires extra care and clear handling rules.</FeatureCard>
          <FeatureCard icon={Icons.FileSearch} title="Hard to measure impact">Ad-hoc processes make it hard to review program outcomes and iterate.</FeatureCard>
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading eyebrow="How PsyTech helps" title="Structured support that respects privacy" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.ClipboardList} title="Structured intake/support questionnaires">Consistent forms that are easier for students to complete and for teams to review.</FeatureCard>
          <FeatureCard icon={Icons.GraduationCap} title="Student-friendly explanations">Clear, non-clinical language that helps users understand what is being asked and why.</FeatureCard>
          <FeatureCard icon={Icons.Lock} title="Privacy-aware summaries">Outputs designed with access control and sensitivity of context in mind.</FeatureCard>
          <FeatureCard icon={Icons.Users} title="Support team review workflows">Clear hand-off from AI-assisted organization to human review and decisions.</FeatureCard>
          <FeatureCard icon={Icons.Sparkles} title="Anonymized insights where appropriate">Group-level patterns (without individual identification) can support program planning.</FeatureCard>
          <FeatureCard icon={Icons.ClipboardCheck} title="Faster preparation for conversations">Teams can walk into one-on-one conversations with a structured starting point.</FeatureCard>
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Boundaries" title="Important limits" />
        <div className="grid md:grid-cols-2 gap-3">
          {[
            "Not emergency support",
            "Not diagnosis",
            "Not a replacement for school counsellors or mental health professionals",
            "Human review is required for support decisions",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-700">{t}</span>
            </div>
          ))}
        </div>
      </Section>

      <CtaBlock
        title="Discuss an education pilot"
        subtitle="We can scope a pilot that respects your institution's safeguarding, consent, and privacy requirements."
        primaryHref="/pilot-program"
        primaryLabel="Explore the pilot program"
        secondaryHref="/#contact"
        secondaryLabel="Contact PsyTech"
        testid="for-education-cta"
      />
    </div>
      <div className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 text-center">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Important:</strong> PsyTech provides digital assessment support and educational information only.
            It does not provide diagnosis, treatment, medication advice, or emergency support.
            AI-generated insights should be reviewed by qualified professionals before clinical use.
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
}
